import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { PenTool, CheckCircle, Loader2, FileText, User, MapPin, DollarSign, AlertCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CounterSignPage() {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);

  const [signatureMethod, setSignatureMethod] = useState("type");
  const [typedSignature, setTypedSignature] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signing, setSigning] = useState(false);

  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setError("Missing counter-sign token");
      setLoading(false);
      return;
    }
    fetch(`${API_URL}/api/public/quotes/${quoteId}/counter-sign?token=${token}`)
      .then(res => {
        if (!res.ok) return res.json().then(d => { throw new Error(d.detail || "Invalid link"); });
        return res.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [quoteId, token]);

  const initCanvas = (canvas) => {
    if (!canvas) return;
    canvasRef.current = canvas;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0F4C81";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    };

    const start = (e) => { e.preventDefault(); isDrawingRef.current = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!isDrawingRef.current) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { isDrawingRef.current = false; };

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("mouseup", end);
    canvas.addEventListener("mouseleave", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  };

  const handleSign = async () => {
    let signatureData;
    if (signatureMethod === "type") {
      const canvas = document.createElement("canvas");
      canvas.width = 400; canvas.height = 100;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 400, 100);
      ctx.font = 'italic 36px "Georgia", serif';
      ctx.fillStyle = "#0F4C81";
      ctx.fillText(typedSignature, 20, 60);
      signatureData = canvas.toDataURL();
    } else {
      signatureData = canvasRef.current?.toDataURL();
    }

    if (!signatureData) return;
    setSigning(true);
    try {
      const res = await fetch(`${API_URL}/api/public/quotes/${quoteId}/counter-sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, signature: signatureData, name: signerName || typedSignature }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || "Failed to sign");
      }
      setSigned(true);
    } catch (e) {
      alert(e.message);
    } finally {
      setSigning(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-800 mb-2">Unable to Load</h2>
          <p className="text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (signed) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2" data-testid="counter-sign-success">Counter-Signature Complete!</h2>
          <p className="text-slate-500 mb-4">
            The agreement for Quote #{data.quote.quote_number} is now fully executed.
          </p>
          <a
            href={`/public/quote/${quoteId}`}
            className="text-[#0F4C81] underline text-sm"
          >
            View the full quote
          </a>
        </CardContent>
      </Card>
    </div>
  );

  const { quote, contact, project, company, timeline } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#0F4C81] text-white">
        <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8 text-center">
          <h1 className="text-xl sm:text-2xl font-bold mb-1" data-testid="counter-sign-heading">
            Counter-Sign Quote #{quote.quote_number}
          </h1>
          <p className="text-blue-200 text-sm">
            {contact.name} signed this quote — add your counter-signature below
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Timeline */}
        {timeline && timeline.length > 0 && (
          <Card>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto" data-testid="counter-sign-timeline">
                {timeline.map((event, i) => (
                  <React.Fragment key={event.step}>
                    <div className="flex flex-col items-center min-w-[60px] sm:min-w-[80px]">
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        event.done
                          ? "bg-emerald-500 text-white"
                          : event.step === "counter_signed"
                          ? "bg-amber-400 text-white ring-2 ring-amber-200 animate-pulse"
                          : "bg-slate-200 text-slate-400"
                      }`}>
                        {event.done ? <CheckCircle className="w-4 h-4" /> : i + 1}
                      </div>
                      <span className={`text-[10px] sm:text-xs mt-1 text-center leading-tight ${
                        event.done ? "text-slate-700 font-medium" : "text-slate-400"
                      }`}>
                        {event.label}
                      </span>
                    </div>
                    {i < timeline.length - 1 && (
                      <div className={`flex-1 h-0.5 min-w-[16px] ${
                        event.done ? "bg-emerald-400" : "bg-slate-200"
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quote Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" /> Quote Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-400 text-xs uppercase">Customer</span>
                <p className="font-medium flex items-center gap-1"><User className="w-3 h-3" /> {contact.name}</p>
              </div>
              <div>
                <span className="text-slate-400 text-xs uppercase">Total</span>
                <p className="font-bold text-[#0F4C81] text-lg flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />{(quote.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
              {project.address && (
                <div className="col-span-2">
                  <span className="text-slate-400 text-xs uppercase">Address</span>
                  <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {project.address}</p>
                </div>
              )}
            </div>

            {/* Signed line-item breakdown — matches the "Signed Summary"
                the customer sees on the portal and the itemized list in
                the notification email. */}
            {Array.isArray(quote.approved_breakdown) && quote.approved_breakdown.length > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="counter-sign-summary">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-[#0F4C81]">Signed Line Items</p>
                </div>
                <div className="divide-y divide-slate-100">
                  {quote.approved_breakdown.map((row, i) => {
                    const kind = (row.kind || "").toLowerCase();
                    const isDiscount = kind === "discount";
                    const amt = Number(row.amount || 0);
                    const amtStr = isDiscount
                      ? `-$${Math.abs(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : `$${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    const label = row.label || (isDiscount ? "Discount" : kind === "addon" ? "Add-on" : "Option");
                    const badge = isDiscount ? "Discount" : kind === "addon" ? (row.mandatory ? "Add-on • Required" : "Add-on") : "Option";
                    return (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`counter-sign-row-${i}`}>
                        <div className="min-w-0 pr-3">
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{badge}</p>
                          <p className="text-slate-800 truncate">{label}</p>
                        </div>
                        <span className={`font-semibold whitespace-nowrap ${isDiscount ? "text-emerald-600" : "text-[#0F4C81]"}`}>{amtStr}</span>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between px-3 py-2.5 bg-[#0F4C81]/5">
                    <span className="text-xs uppercase tracking-wider font-bold text-slate-700">Approved Total</span>
                    <span className="text-base font-extrabold text-[#0F4C81]" data-testid="counter-sign-total">
                      ${(quote.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Customer's signature */}
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium">Customer Signature</span>
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Signed</Badge>
              </div>
              <div className="flex items-center gap-3">
                {quote.customer_signature?.signature && (
                  <img src={quote.customer_signature.signature} alt="Customer Signature" className="h-10 border rounded" />
                )}
                <div className="text-xs text-slate-500">
                  <p>{quote.customer_signature?.name}</p>
                  {quote.customer_signature?.signed_at && (
                    <p>{new Date(quote.customer_signature.signed_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Counter-Sign Card */}
        <Card className="border-2 border-[#0F4C81]/20">
          <CardHeader className="bg-[#0F4C81]/5 border-b">
            <CardTitle className="text-base flex items-center gap-2">
              <PenTool className="w-4 h-4" /> Your Counter-Signature
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-5 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Your Full Name</label>
              <Input
                value={signerName}
                onChange={e => setSignerName(e.target.value)}
                placeholder={company.name || "Your name"}
                data-testid="counter-sign-name"
              />
            </div>

            {/* Signature method tabs */}
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              <button
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  signatureMethod === "type" ? "bg-[#0F4C81] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
                onClick={() => setSignatureMethod("type")}
              >
                Type
              </button>
              <button
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  signatureMethod === "draw" ? "bg-[#0F4C81] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
                onClick={() => setSignatureMethod("draw")}
              >
                Draw
              </button>
            </div>

            {signatureMethod === "type" ? (
              <div className="space-y-2">
                <Input
                  value={typedSignature}
                  onChange={e => setTypedSignature(e.target.value)}
                  placeholder="Type your signature"
                  className="font-serif italic text-xl"
                  data-testid="counter-sign-typed"
                />
                {typedSignature && (
                  <div className="border rounded-lg p-3 bg-slate-50 text-center">
                    <span className="font-serif italic text-2xl text-[#0F4C81]">{typedSignature}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <canvas
                  ref={initCanvas}
                  width={500}
                  height={150}
                  className="w-full border rounded-lg cursor-crosshair bg-white"
                  style={{ touchAction: "none" }}
                  data-testid="counter-sign-canvas"
                />
                <button onClick={clearCanvas} className="text-xs text-slate-400 hover:text-slate-600 underline">
                  Clear
                </button>
              </div>
            )}

            <Button
              className="w-full h-12 bg-[#0F4C81] hover:bg-[#0D4270] text-white"
              onClick={handleSign}
              disabled={signing || !signerName || (signatureMethod === "type" ? !typedSignature : false)}
              data-testid="counter-sign-submit"
            >
              {signing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <PenTool className="w-5 h-5 mr-2" />}
              Counter-Sign & Finalize
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
